import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { PublicLayout } from '@/components/PublicLayout';
import { ProductCard } from '@/components/ProductCard';
import { Kicker } from '@/components/Kicker';
import { Search } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAuth } from '@/lib/auth';

export default function CatalogPage({ portal = false }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('all');

  const productsQ = useQuery({
    queryKey: ['products', portal ? 'auth' : 'public'],
    queryFn: async () => (await api.get('/products')).data,
  });
  const categoriesQ = useQuery({
    queryKey: ['categories'],
    queryFn: async () => (await api.get('/products/meta/categories')).data,
  });

  const items = productsQ.data?.items || [];
  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return items.filter((p) => {
      if (cat !== 'all' && String(p.category_id) !== String(cat)) return false;
      if (!ql) return true;
      return (
        (p.name || '').toLowerCase().includes(ql) ||
        (p.default_code || '').toLowerCase().includes(ql) ||
        (p.category_path || '').toLowerCase().includes(ql)
      );
    });
  }, [items, q, cat]);

  return (
    <PublicLayout>
      <section className="pn-container pt-10">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#4A4D52]" />
            <input
              data-testid="catalog-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t('catalog.search_placeholder')}
              className="h-11 w-full rounded-full border border-[#E4E9F0] bg-white pl-11 pr-4 text-sm outline-none focus:border-[#E2611C]"
            />
          </div>
          <div className="sm:w-64">
            <Select value={cat} onValueChange={setCat}>
              <SelectTrigger data-testid="catalog-category-select" className="h-11 rounded-full border-[#E4E9F0]">
                <SelectValue placeholder={t('catalog.category_all')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('catalog.category_all')}</SelectItem>
                {(categoriesQ.data?.items || []).map((c) => (
                  <SelectItem key={c.id} value={String(c.id)}>{c.complete_name || c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="mt-4 text-xs text-[#4A4D52]" data-testid="catalog-count">
          {t('catalog.count_other', { count: filtered.length })}
        </div>

        <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {productsQ.isLoading && Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="pn-card h-[420px] animate-pulse" data-testid="loading-skeleton" />
          ))}
          {!productsQ.isLoading && filtered.length === 0 && (
            <div className="col-span-full pn-card p-10 text-center text-sm text-[#4A4D52]">
              {t('catalog.empty')}
              <div className="mt-4">
                <button data-testid="catalog-empty-reset-button" onClick={() => { setQ(''); setCat('all'); }} className="pn-btn-outline">
                  {t('catalog.reset')}
                </button>
              </div>
            </div>
          )}
          {!productsQ.isLoading && filtered.map((p) => (
            <ProductCard key={p.id} product={p} isAuth={productsQ.data?.is_authenticated} portal={portal} />
          ))}
        </div>
      </section>
    </PublicLayout>
  );
}
